import { useEffect } from "react";
import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";

// COMPONENTS
import KOGImage from "@/main/components/shared/kogImage/kogImage";
import KogCheckbox from "@/main/components/shared/checkbox/kogCheckbox";
import KogInputAmountControlled from "@/main/components/shared/inputText/inputAmountControlled";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import QuoteProcurementPartInfo from "@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfo";
import CanceledBySupplierWarning from "@/main/components/shared/canceledBySupplierWarning/canceledBySupplierWarning";
import ResultNotFoundComponent from "@/main/components/shared/resultNotFound/resultNotFoundComponent";

// UTILS/SERVICES
import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { usePartTypeReducer } from "@/main/store/partType";
import { formatCurrency } from "@/main/services/utilsNumber";
import { useBrandReducer } from "@/main/store/brand";
import { PartTypeEnum } from "@/main/services/enum/PartTypeEnum";

// STYLES & COMPONENTS LOCAL
import { TypeFormStateReassignBodyshop, TypePartForm } from "../modalReassignBodyshop";
import * as S from "./styles";

interface IItemPartProps {
	index: number;
	fieldForm: TypePartForm;
}
function ItemPart({ index, fieldForm }: IItemPartProps) {
	const { control, getValues, clearErrors, setValue } = useFormContext<TypeFormStateReassignBodyshop>();

	const { partType: partTypeReducer } = usePartTypeReducer();
	const { brand: brandReducer } = useBrandReducer();

	const watcherIsCheck = useWatch({
		control,
		name: `parts.${index}.isCheck`,
	});
	const watcherPartType = useWatch({
		control,
		name: `parts.${index}.partType`,
	});

	function handleClickSelect() {
		const cloneOptionsSelected = getValues("partsSelected");
		const index = cloneOptionsSelected.indexOf(fieldForm.guidProcurementSupplierItem);

		if (index > -1) cloneOptionsSelected.splice(index, 1);
		else cloneOptionsSelected.push(fieldForm.guidProcurementSupplierItem);

		setValue("partsSelected", cloneOptionsSelected);
	}

	useEffect(() => {
		clearErrors(`parts.${index}.price`);
		clearErrors(`parts.${index}.partType`);
	}, [watcherIsCheck]);
	useEffect(() => {
		if (isNullUndefined(watcherPartType) || Number(watcherPartType.default) === PartTypeEnum.OEM) {
			setValue(`parts.${index}.partBrand`, null);
		}
	}, [watcherPartType?.default]);

	const isDiffOEM = !isNullUndefined(watcherPartType) && Number(watcherPartType.default) !== PartTypeEnum.OEM;
	return (
		<S.RowItem>
			<Controller
				control={control}
				name={`parts.${index}.isCheck`}
				render={({ field }) => (
					<S.AreaInfo
						onClick={() => {
							handleClickSelect();
							field.onChange(!field.value);
						}}
					>
						<KOGImage src={fieldForm.infoPartResponse.image} alt="" />
						<S.NamePricePart>
							<QuoteProcurementPartInfo
								data={fieldForm.infoPartResponse.quoteProcurementPartInfoResponse}
								renderType="column"
								isTooltip
							/>
							{fieldForm.infoPartResponse.canceledBySupplier && <CanceledBySupplierWarning />}
							{!isNullUndefined(fieldForm.infoPartResponse.currentPrice) && (
								<span>
									{getTranslateByKey("information.currentPrice")}:{" "}
									{formatCurrency(fieldForm.infoPartResponse.currentPrice)}
								</span>
							)}
						</S.NamePricePart>
						<KogCheckbox
							id={`id-searchOrder-checkbox-reassignBodyshop-select-${index}`}
							checked={field.value}
							value={field.value}
							onBlur={field.onBlur}
						/>
					</S.AreaInfo>
				)}
			/>
			{watcherIsCheck && (
				<S.AreaForm $isThreeColumns={isDiffOEM}>
					<KogInputAmountControlled
						required={watcherIsCheck}
						typeInput={"filled"}
						id="id-searchOrder-input-reassignBodyshop"
						name={`parts.${index}.price`}
						label={getTranslateByKey("information.price")}
						control={control}
					/>
					<KogAutoCompleteControlled
						id="id-searchOrder-input-reassignBodyshop"
						nameCustom={`parts.${index}.partType`}
						label={getTranslateByKey("information.partTypes")}
						control={control}
						rulesCustom={{ required: watcherIsCheck }}
						disabled={false}
						disabledMessage={false}
						hideValue={false}
						isDefaultValueUnique
						setValueCustom={setValue}
						options={partTypeReducer.data}
						isLoading={partTypeReducer.isLoading}
						onChangeCustom={() => {}}
					/>
					{isDiffOEM && (
						<KogAutoCompleteControlled
							id="id-searchOrder-input-partBrandName"
							nameCustom={`parts.${index}.partBrand`}
							placeholder={getTranslateByKey("information.brand")}
							label={getTranslateByKey("information.brand")}
							control={control}
							rulesCustom={{ required: false }}
							isDefaultValueUnique
							setValueCustom={setValue}
							options={brandReducer.data}
							isLoading={brandReducer.isLoading}
							onChangeCustom={() => {}}
						/>
					)}
				</S.AreaForm>
			)}
		</S.RowItem>
	);
}

function WrapperSectionParts() {
	const { control } = useFormContext<TypeFormStateReassignBodyshop>();
	const { fields } = useFieldArray({
		control,
		name: "parts",
	});

	if (fields.length === 0) return <ResultNotFoundComponent />;
	return (
		<S.Container>
			{fields.map((fieldForm, index) => (
				<ItemPart key={fieldForm.id} index={index} fieldForm={fieldForm} />
			))}
		</S.Container>
	);
}

export default WrapperSectionParts;
